import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Edit, Trash2, Search, Package, Image, Upload, X, DollarSign, Truck, Download, GraduationCap, Crown, Heart, Zap, CreditCard, Key, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { getProductImageUrl, resolveMediaUrl } from '@/lib/productMedia';

function normalizeProductMedia(form) {
  const payload = { ...form };
  const images = [...(payload.images || [])].filter(Boolean);
  if (payload.thumbnail_url) {
    if (!images.includes(payload.thumbnail_url)) {
      payload.images = [payload.thumbnail_url, ...images];
    } else {
      payload.images = images;
    }
  } else if (images.length) {
    payload.thumbnail_url = images[0];
    payload.images = images;
  }
  return payload;
}

const TYPES = [
  { value: 'all', label: 'Semua Tipe' },
  { value: 'physical', label: 'Produk Fisik', icon: Package, color: 'bg-blue-100 text-blue-700' },
  { value: 'digital', label: 'Produk Digital', icon: Download, color: 'bg-violet-100 text-violet-700' },
  { value: 'course', label: 'Kursus / LMS', icon: GraduationCap, color: 'bg-indigo-100 text-indigo-700' },
  { value: 'membership', label: 'Membership', icon: Crown, color: 'bg-teal-100 text-teal-700' },
  { value: 'donation', label: 'Donasi', icon: Heart, color: 'bg-pink-100 text-pink-700' },
  { value: 'ppob', label: 'PPOB', icon: Zap, color: 'bg-amber-100 text-amber-700' },
];

const CATEGORIES = [
  'Elektronik', 'Fashion', 'Makanan & Minuman', 'Kesehatan', 'Kecantikan', 
  'Olahraga', 'Hobi', 'Otomotif', 'Rumah Tangga', 'Buku', 'Software', 'Lainnya'
];

const defaultForm = {
  // Basic Info
  name: '',
  type: 'physical',
  description: '',
  short_description: '',
  category: '',
  sku: '',
  barcode: '',
  
  // Pricing
  price: 0,
  sale_price: null,
  cost_price: 0,
  
  // Physical Product
  stock: 0,
  weight: 0,
  length: 0,
  width: 0,
  height: 0,
  track_stock: true,
  allow_backorder: false,
  
  // Digital Product
  digital_file_url: '',
  digital_file_urls: [],
  download_limit: -1,
  download_expiry_days: 30,
  
  // Course/LMS
  course_id: '',
  access_duration_days: 365,
  
  // Membership
  membership_duration_days: 30,
  membership_level: 'basic',
  membership_recurring: false,
  membership_benefits: '',
  
  // Donation
  donation_target: 0,
  donation_campaign_name: '',
  donation_end_date: '',
  allow_custom_amount: true,
  suggested_amounts: '10000,25000,50000,100000',
  
  // PPOB
  ppob_category: '',
  ppob_provider_code: '',
  ppob_markup_type: 'fixed',
  ppob_markup_value: 500,
  
  // High-Ticket
  is_high_ticket: false,
  payment_plan_enabled: false,
  booking_fee_type: 'percentage',
  booking_fee_value: 10,
  dp_percentage: 30,
  installment_type: 'fixed',
  installment_count: 6,
  max_installments: 12,
  installment_interval_days: 30,
  late_policy: 'none',
  late_penalty_percentage: 0,
  
  // Media
  images: [],
  thumbnail_url: '',
  video_url: '',
  
  // SEO
  seo_title: '',
  seo_description: '',
  slug: '',
  
  // Status
  is_active: true,
  is_featured: false,
  
  // Variants (for future)
  has_variants: false,
  variants: [],

  // Reseller Pricing
  reseller_prices: [],
};

export default function TenantProducts() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [activeTab, setActiveTab] = useState('basic');
  const [courses, setCourses] = useState([]);
  const [serialKeyDialog, setSerialKeyDialog] = useState(false);
  const [serialKeyProduct, setSerialKeyProduct] = useState(null);
  const [serialKeyData, setSerialKeyData] = useState(null);
  const [newSerialKeys, setNewSerialKeys] = useState('');
  const [resellerTiers, setResellerTiers] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);

  const fetchProducts = async () => {
    try {
      const res = await api.get('/tenant/products', { params: { search, type: typeFilter === 'all' ? '' : typeFilter } });
      setProducts(res.data.products); 
      setTotal(res.data.total);
    } catch (e) { /* handled by interceptor */ }
  };

  const fetchCourses = async () => {
    try {
      const res = await api.get('/tenant/courses');
      setCourses(res.data.courses || []);
    } catch (e) { console.log('No courses'); }
  };

  useEffect(() => { fetchProducts(); }, [search, typeFilter]);
  useEffect(() => { fetchCourses(); }, []);
  useEffect(() => {
    api.get('/tenant/reseller-tiers').then(r => setResellerTiers(r.data.tiers || [])).catch(() => {});
  }, []);

  const openSerialKeys = async (product) => {
    setSerialKeyProduct(product);
    setNewSerialKeys('');
    try {
      const res = await api.get(`/tenant/products/${product.id}/serial-keys`);
      setSerialKeyData(res.data);
    } catch { setSerialKeyData({ total: 0, available: 0, assigned: 0, keys: [], assigned_keys: [] }); }
    setSerialKeyDialog(true);
  };

  const addSerialKeys = async () => {
    if (!newSerialKeys.trim() || !serialKeyProduct) return;
    const keys = newSerialKeys.split('\n').map(k => k.trim()).filter(Boolean);
    await api.post(`/tenant/products/${serialKeyProduct.id}/serial-keys`, { keys });
    setNewSerialKeys('');
    const res = await api.get(`/tenant/products/${serialKeyProduct.id}/serial-keys`);
    setSerialKeyData(res.data);
  };

  const deleteSerialKey = async (key) => {
    if (!window.confirm('Hapus serial key ini?')) return;
    try {
      await api.delete(`/tenant/products/${serialKeyProduct.id}/serial-keys/${encodeURIComponent(key)}`);
      const res = await api.get(`/tenant/products/${serialKeyProduct.id}/serial-keys`);
      setSerialKeyData(res.data);
    } catch (err) { alert(err.response?.data?.detail || 'Gagal menghapus'); }
  };

  const handleSave = async () => {
    try {
      const payload = normalizeProductMedia(form);
      if (editing) await api.put(`/tenant/products/${editing}`, payload);
      else await api.post('/tenant/products', payload);
      toast.success(editing ? 'Produk diperbarui' : 'Produk ditambahkan');
      setShowDialog(false); 
      setEditing(null); 
      setForm(defaultForm); 
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    }
  };

  const handleUploadProductImage = async (e, target = 'thumbnail') => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Pilih file gambar (JPG, PNG, WEBP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran gambar maksimal 5MB');
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/tenant/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.url;
      if (target === 'gallery') {
        setForm((prev) => ({ ...prev, images: [...(prev.images || []), url] }));
      } else {
        setForm((prev) => ({
          ...prev,
          thumbnail_url: url,
          images: prev.images?.includes(url) ? prev.images : [url, ...(prev.images || [])],
        }));
      }
      toast.success('Gambar produk diupload');
    } catch {
      toast.error('Gagal upload gambar');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Hapus produk ini?')) { 
      await api.delete(`/tenant/products/${id}`); 
      fetchProducts(); 
    }
  };

  const edit = (p) => { 
    setEditing(p.id); 
    setForm({ ...defaultForm, ...p }); 
    setActiveTab('basic');
    setShowDialog(true); 
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const getTypeInfo = (type) => TYPES.find(t => t.value === type) || TYPES[1];

  return (
    <div data-testid="tenant-products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Produk ({total})</h1>
          <p className="text-sm text-muted-foreground">Kelola semua produk toko Anda</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(defaultForm); setActiveTab('basic'); setShowDialog(true); }} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-product-btn">
          <Plus size={16} className="mr-2" /> Tambah Produk
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cari produk..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="product-search-input" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]" data-testid="product-type-filter">
                <SelectValue placeholder="Tipe produk" />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    <span className="flex items-center gap-2">
                      {t.icon && <t.icon size={14} />}
                      {t.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="products-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Produk</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Tipe</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Harga</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Stok/Info</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const typeInfo = getTypeInfo(p.type);
                  const thumb = getProductImageUrl(p);
                  return (
                    <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                            {thumb ? (
                              <img src={thumb} alt={p.name} className="w-full h-full object-cover" />
                            ) : (
                              <Package size={20} className="text-slate-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{p.name}</p>
                            <p className="text-xs text-slate-500">{p.sku || p.category || '-'}{p.barcode ? ` · ${p.barcode}` : ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={typeInfo.color}>
                          {typeInfo.icon && <typeInfo.icon size={12} className="mr-1" />}
                          {typeInfo.label}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {p.sale_price ? (
                          <div>
                            <span className="line-through text-slate-400 text-xs">Rp {fmt(p.price)}</span>
                            <br />
                            <span className="text-emerald-600 font-medium">Rp {fmt(p.sale_price)}</span>
                          </div>
                        ) : (
                          <span className="font-medium">Rp {fmt(p.price)}</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {p.type === 'physical' && <span>{p.stock} unit</span>}
                        {p.type === 'digital' && <span className="text-violet-600">Download</span>}
                        {p.type === 'course' && <span className="text-indigo-600">{p.access_duration_days || 365} hari</span>}
                        {p.type === 'membership' && <span className="text-teal-600">{p.membership_duration_days || 30} hari</span>}
                        {p.type === 'donation' && <span className="text-pink-600">Target: Rp {fmt(p.donation_target)}</span>}
                        {p.type === 'ppob' && <span className="text-amber-600">{p.ppob_category || 'PPOB'}</span>}
                        {p.is_high_ticket && <span className="text-emerald-600 font-medium">🏷️ Cicilan</span>}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium w-fit ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {p.is_active ? 'Aktif' : 'Nonaktif'}
                          </span>
                          {p.is_featured && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium w-fit bg-amber-100 text-amber-700">
                              Featured
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          {p.type === 'digital' && (
                            <Button variant="ghost" size="icon" onClick={() => openSerialKeys(p)} title="Serial Keys">
                              <Key size={14} className="text-violet-500" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => edit(p)} data-testid={`edit-product-${p.id}`}>
                            <Edit size={14} />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)} data-testid={`delete-product-${p.id}`}>
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!products.length && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-400">
                      Belum ada produk
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Product Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Produk' : 'Tambah Produk Baru'}</DialogTitle>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="basic">Dasar</TabsTrigger>
              <TabsTrigger value="pricing">Harga</TabsTrigger>
              <TabsTrigger value="details">Detail</TabsTrigger>
              <TabsTrigger value="media">Media</TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div>
                <Label>Tipe Produk *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="product-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.filter(t => t.value !== 'all').map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="flex items-center gap-2">
                          {t.icon && <t.icon size={14} />}
                          {t.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {form.type === 'physical' && 'Produk dengan stok dan pengiriman fisik'}
                  {form.type === 'digital' && 'File digital yang bisa didownload'}
                  {form.type === 'course' && 'Kursus online dengan akses terbatas'}
                  {form.type === 'membership' && 'Langganan dengan benefit khusus'}
                  {form.type === 'donation' && 'Penggalangan dana / donasi'}
                  {form.type === 'ppob' && 'Produk PPOB seperti pulsa, token PLN'}
                </p>
              </div>

              <div>
                <Label>Nama Produk *</Label>
                <Input 
                  value={form.name} 
                  onChange={(e) => setForm({ ...form, name: e.target.value })} 
                  placeholder="Masukkan nama produk"
                  data-testid="product-name-input" 
                />
              </div>

              <div>
                <Label>Deskripsi Singkat</Label>
                <Input 
                  value={form.short_description} 
                  onChange={(e) => setForm({ ...form, short_description: e.target.value })} 
                  placeholder="Ringkasan produk (maks 160 karakter)"
                  maxLength={160}
                />
              </div>

              <div>
                <Label>Deskripsi Lengkap</Label>
                <Textarea 
                  value={form.description} 
                  onChange={(e) => setForm({ ...form, description: e.target.value })} 
                  rows={4}
                  placeholder="Deskripsi detail produk..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Kategori</Label>
                  <Select value={form.category || 'none'} onValueChange={(v) => setForm({ ...form, category: v === 'none' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih kategori" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Tidak ada --</SelectItem>
                      {CATEGORIES.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>SKU / Kode Produk</Label>
                  <Input 
                    value={form.sku} 
                    onChange={(e) => setForm({ ...form, sku: e.target.value })} 
                    placeholder="SKU-001"
                  />
                </div>
              </div>

              {form.type === 'physical' && (
                <div>
                  <Label>Barcode</Label>
                  <Input 
                    value={form.barcode} 
                    onChange={(e) => setForm({ ...form, barcode: e.target.value })} 
                    placeholder="Masukkan barcode produk (EAN/UPC)"
                  />
                </div>
              )}

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={form.is_active} 
                    onCheckedChange={(v) => setForm({ ...form, is_active: v })} 
                  />
                  <Label>Produk Aktif</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={form.is_featured} 
                    onCheckedChange={(v) => setForm({ ...form, is_featured: v })} 
                  />
                  <Label>Produk Unggulan</Label>
                </div>
              </div>
            </TabsContent>

            {/* Pricing Tab */}
            <TabsContent value="pricing" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Harga Jual (Rp) *</Label>
                  <Input 
                    type="number" 
                    value={form.price} 
                    onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} 
                    data-testid="product-price-input" 
                  />
                </div>
                <div>
                  <Label>Harga Promo (Rp)</Label>
                  <Input 
                    type="number" 
                    value={form.sale_price || ''} 
                    onChange={(e) => setForm({ ...form, sale_price: parseInt(e.target.value) || null })} 
                    placeholder="Kosongkan jika tidak promo"
                  />
                </div>
                <div>
                  <Label>Harga Modal (Rp)</Label>
                  <Input 
                    type="number" 
                    value={form.cost_price || ''} 
                    onChange={(e) => setForm({ ...form, cost_price: parseInt(e.target.value) || 0 })} 
                    placeholder="Untuk kalkulasi profit"
                  />
                </div>
              </div>

              {form.cost_price > 0 && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm">
                    <strong>Profit:</strong> Rp {fmt((form.sale_price || form.price) - form.cost_price)} 
                    ({Math.round(((form.sale_price || form.price) - form.cost_price) / form.cost_price * 100)}%)
                  </p>
                </div>
              )}

              {/* High-Ticket / Cicilan Option - Available for ALL product types */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={form.is_high_ticket} 
                    onCheckedChange={(v) => setForm({ ...form, is_high_ticket: v })} 
                  />
                  <Label className="font-medium">Aktifkan Sistem Cicilan / High-Ticket</Label>
                </div>
                <p className="text-xs text-muted-foreground">Aktifkan untuk produk mahal yang bisa dibayar secara bertahap (booking fee, DP, cicilan)</p>
                
                {form.is_high_ticket && (
                  <div className="space-y-4 pt-2 border-t">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Tipe Booking Fee</Label>
                        <Select value={form.booking_fee_type} onValueChange={(v) => setForm({ ...form, booking_fee_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Persentase (%)</SelectItem>
                            <SelectItem value="fixed">Nominal Tetap (Rp)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Nilai Booking Fee</Label>
                        <Input 
                          type="number" 
                          value={form.booking_fee_value} 
                          onChange={(e) => setForm({ ...form, booking_fee_value: parseInt(e.target.value) || 0 })} 
                        />
                        {form.booking_fee_type === 'percentage' && (
                          <p className="text-xs text-muted-foreground mt-1">Booking: Rp {fmt(form.price * form.booking_fee_value / 100)}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>DP (%)</Label>
                        <Input 
                          type="number" 
                          value={form.dp_percentage} 
                          onChange={(e) => setForm({ ...form, dp_percentage: parseInt(e.target.value) || 30 })} 
                          min={10} max={90}
                        />
                        <p className="text-xs text-muted-foreground mt-1">DP: Rp {fmt(form.price * form.dp_percentage / 100)}</p>
                      </div>
                      <div>
                        <Label>Jumlah Cicilan</Label>
                        <Input 
                          type="number" 
                          value={form.installment_count} 
                          onChange={(e) => setForm({ ...form, installment_count: parseInt(e.target.value) || 6 })} 
                          min={2} max={24}
                        />
                        {form.price > 0 && form.dp_percentage > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Per cicilan: Rp {fmt(Math.round((form.price - form.price * form.dp_percentage / 100) / (form.installment_count || 1)))}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label>Tipe Cicilan</Label>
                        <Select value={form.installment_type} onValueChange={(v) => setForm({ ...form, installment_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">Fixed</SelectItem>
                            <SelectItem value="flexible">Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Late Policy</Label>
                        <Select value={form.late_policy} onValueChange={(v) => setForm({ ...form, late_policy: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Tidak Ada</SelectItem>
                            <SelectItem value="penalty">Denda (%)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {form.late_policy === 'penalty' && (
                        <div>
                          <Label>Denda Keterlambatan (%)</Label>
                          <Input 
                            type="number" 
                            value={form.late_penalty_percentage} 
                            onChange={(e) => setForm({ ...form, late_penalty_percentage: parseInt(e.target.value) || 0 })} 
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* PPOB Markup */}
              {form.type === 'ppob' && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium">Pengaturan Markup PPOB</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tipe Markup</Label>
                      <Select value={form.ppob_markup_type} onValueChange={(v) => setForm({ ...form, ppob_markup_type: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Nominal Tetap (Rp)</SelectItem>
                          <SelectItem value="percentage">Persentase (%)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Nilai Markup</Label>
                      <Input 
                        type="number" 
                        value={form.ppob_markup_value} 
                        onChange={(e) => setForm({ ...form, ppob_markup_value: parseInt(e.target.value) || 0 })} 
                      />
                    </div>
                  </div>
                </div>
              )}
              )}

              {/* Reseller Pricing */}
              {resellerTiers.length > 0 && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <DollarSign size={16} /> Harga Reseller per Tier
                  </h4>
                  <p className="text-sm text-muted-foreground">Kosongkan untuk menggunakan diskon tier default. Isi untuk override harga khusus produk ini.</p>
                  <div className="grid grid-cols-2 gap-3">
                    {resellerTiers.map(tier => {
                      const existing = (form.reseller_prices || []).find(rp => rp.tier_id === tier.id);
                      return (
                        <div key={tier.id} className="flex items-center gap-2">
                          <Label className="w-24 text-sm">{tier.name} (Lv.{tier.level})</Label>
                          <Input
                            type="number"
                            placeholder={`Default: ${tier.discount_type === 'percentage' ? tier.discount_value + '%' : 'Rp ' + fmt(tier.discount_value)} off`}
                            value={existing?.price || ''}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              const prices = (form.reseller_prices || []).filter(rp => rp.tier_id !== tier.id);
                              if (val > 0) prices.push({ tier_id: tier.id, price: val });
                              setForm({ ...form, reseller_prices: prices });
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-4 mt-4">
              {/* Physical Product Details */}
              {form.type === 'physical' && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Truck size={16} /> Detail Produk Fisik
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Stok</Label>
                      <Input 
                        type="number" 
                        value={form.stock} 
                        onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })} 
                      />
                    </div>
                    <div>
                      <Label>Berat (gram)</Label>
                      <Input 
                        type="number" 
                        value={form.weight} 
                        onChange={(e) => setForm({ ...form, weight: parseInt(e.target.value) || 0 })} 
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Panjang (cm)</Label>
                      <Input 
                        type="number" 
                        value={form.length || ''} 
                        onChange={(e) => setForm({ ...form, length: parseInt(e.target.value) || 0 })} 
                      />
                    </div>
                    <div>
                      <Label>Lebar (cm)</Label>
                      <Input 
                        type="number" 
                        value={form.width || ''} 
                        onChange={(e) => setForm({ ...form, width: parseInt(e.target.value) || 0 })} 
                      />
                    </div>
                    <div>
                      <Label>Tinggi (cm)</Label>
                      <Input 
                        type="number" 
                        value={form.height || ''} 
                        onChange={(e) => setForm({ ...form, height: parseInt(e.target.value) || 0 })} 
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={form.track_stock} 
                        onCheckedChange={(v) => setForm({ ...form, track_stock: v })} 
                      />
                      <Label>Tracking Stok</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={form.allow_backorder} 
                        onCheckedChange={(v) => setForm({ ...form, allow_backorder: v })} 
                      />
                      <Label>Izinkan Backorder</Label>
                    </div>
                  </div>
                </div>
              )}

              {/* Digital Product Details */}
              {form.type === 'digital' && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Download size={16} /> Detail Produk Digital
                  </h4>
                  <div>
                    <Label>URL File Download Utama</Label>
                    <Input 
                      value={form.digital_file_url} 
                      onChange={(e) => setForm({ ...form, digital_file_url: e.target.value })} 
                      placeholder="https://drive.google.com/..." 
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Link Google Drive, Dropbox, atau hosting file lainnya
                    </p>
                  </div>
                  {/* Multiple Download Links */}
                  <div>
                    <Label>Link Download Tambahan</Label>
                    <p className="text-xs text-muted-foreground mb-2">Tambahkan beberapa link file download jika ada lebih dari satu file</p>
                    {(form.digital_file_urls || []).map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2">
                        <Input 
                          value={url.url || ''} 
                          onChange={(e) => {
                            const urls = [...(form.digital_file_urls || [])];
                            urls[idx] = { ...urls[idx], url: e.target.value };
                            setForm({ ...form, digital_file_urls: urls });
                          }} 
                          placeholder="https://..." 
                          className="flex-1"
                        />
                        <Input 
                          value={url.label || ''} 
                          onChange={(e) => {
                            const urls = [...(form.digital_file_urls || [])];
                            urls[idx] = { ...urls[idx], label: e.target.value };
                            setForm({ ...form, digital_file_urls: urls });
                          }} 
                          placeholder="Nama file (opsional)" 
                          className="w-48"
                        />
                        <Button variant="ghost" size="icon" onClick={() => {
                          setForm({ ...form, digital_file_urls: (form.digital_file_urls || []).filter((_, i) => i !== idx) });
                        }}>
                          <X size={14} className="text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => {
                      setForm({ ...form, digital_file_urls: [...(form.digital_file_urls || []), { url: '', label: '' }] });
                    }}>
                      <Plus size={14} className="mr-1" /> Tambah Link
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Batas Download</Label>
                      <Input 
                        type="number" 
                        value={form.download_limit} 
                        onChange={(e) => setForm({ ...form, download_limit: parseInt(e.target.value) })} 
                      />
                      <p className="text-xs text-muted-foreground mt-1">-1 = Unlimited</p>
                    </div>
                    <div>
                      <Label>Masa Aktif (Hari)</Label>
                      <Input 
                        type="number" 
                        value={form.download_expiry_days} 
                        onChange={(e) => setForm({ ...form, download_expiry_days: parseInt(e.target.value) || 30 })} 
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Course Details */}
              {form.type === 'course' && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <GraduationCap size={16} /> Detail Kursus
                  </h4>
                  <div>
                    <Label>Pilih Kursus</Label>
                    <Select value={form.course_id || 'none'} onValueChange={(v) => setForm({ ...form, course_id: v === 'none' ? '' : v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Pilih kursus yang sudah dibuat" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Pilih Kursus --</SelectItem>
                        {courses.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Buat kursus terlebih dahulu di menu Kursus/LMS
                    </p>
                  </div>
                  <div>
                    <Label>Durasi Akses (Hari)</Label>
                    <Input 
                      type="number" 
                      value={form.access_duration_days} 
                      onChange={(e) => setForm({ ...form, access_duration_days: parseInt(e.target.value) || 365 })} 
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      0 = Selamanya
                    </p>
                  </div>
                </div>
              )}

              {/* Membership Details */}
              {form.type === 'membership' && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Crown size={16} /> Detail Membership
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Durasi (Hari)</Label>
                      <Input 
                        type="number" 
                        value={form.membership_duration_days} 
                        onChange={(e) => setForm({ ...form, membership_duration_days: parseInt(e.target.value) || 30 })} 
                      />
                    </div>
                    <div>
                      <Label>Level</Label>
                      <Select value={form.membership_level} onValueChange={(v) => setForm({ ...form, membership_level: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="silver">Silver</SelectItem>
                          <SelectItem value="gold">Gold</SelectItem>
                          <SelectItem value="platinum">Platinum</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Benefit / Keuntungan</Label>
                    <Textarea 
                      value={form.membership_benefits} 
                      onChange={(e) => setForm({ ...form, membership_benefits: e.target.value })} 
                      rows={3}
                      placeholder="- Akses konten eksklusif&#10;- Diskon 10% semua produk&#10;- Support prioritas"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={form.membership_recurring} 
                      onCheckedChange={(v) => setForm({ ...form, membership_recurring: v })} 
                    />
                    <Label>Recurring / Langganan Otomatis</Label>
                  </div>
                </div>
              )}

              {/* Donation Details */}
              {form.type === 'donation' && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Heart size={16} /> Detail Donasi
                  </h4>
                  <div>
                    <Label>Nama Kampanye</Label>
                    <Input 
                      value={form.donation_campaign_name} 
                      onChange={(e) => setForm({ ...form, donation_campaign_name: e.target.value })} 
                      placeholder="Bantuan Bencana Alam"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Target Donasi (Rp)</Label>
                      <Input 
                        type="number" 
                        value={form.donation_target} 
                        onChange={(e) => setForm({ ...form, donation_target: parseInt(e.target.value) || 0 })} 
                      />
                    </div>
                    <div>
                      <Label>Tanggal Berakhir</Label>
                      <Input 
                        type="date" 
                        value={form.donation_end_date} 
                        onChange={(e) => setForm({ ...form, donation_end_date: e.target.value })} 
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Nominal Saran (pisahkan dengan koma)</Label>
                    <Input 
                      value={form.suggested_amounts} 
                      onChange={(e) => setForm({ ...form, suggested_amounts: e.target.value })} 
                      placeholder="10000,25000,50000,100000"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={form.allow_custom_amount} 
                      onCheckedChange={(v) => setForm({ ...form, allow_custom_amount: v })} 
                    />
                    <Label>Izinkan Nominal Bebas</Label>
                  </div>
                </div>
              )}

              {/* PPOB Details */}
              {form.type === 'ppob' && (
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Zap size={16} /> Detail PPOB
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Kategori PPOB</Label>
                      <Select value={form.ppob_category || 'none'} onValueChange={(v) => setForm({ ...form, ppob_category: v === 'none' ? '' : v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Pilih kategori" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Pilih --</SelectItem>
                          <SelectItem value="pulsa">Pulsa</SelectItem>
                          <SelectItem value="data">Paket Data</SelectItem>
                          <SelectItem value="pln">Token PLN</SelectItem>
                          <SelectItem value="game">Voucher Game</SelectItem>
                          <SelectItem value="emoney">E-Money</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Kode Provider</Label>
                      <Input 
                        value={form.ppob_provider_code} 
                        onChange={(e) => setForm({ ...form, ppob_provider_code: e.target.value })} 
                        placeholder="TSEL10, XL25, PLN50..."
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    PPOB produk akan terhubung otomatis dengan Tripay PPOB
                  </p>
                </div>
              )}
            </TabsContent>

            {/* Media Tab */}
            <TabsContent value="media" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Gambar Utama / Thumbnail</Label>
                {form.thumbnail_url ? (
                  <div className="relative w-full max-w-xs rounded-lg overflow-hidden border border-slate-200">
                    <img src={resolveMediaUrl(form.thumbnail_url)} alt="Thumbnail" className="w-full h-40 object-cover" />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setForm({ ...form, thumbnail_url: '' })}
                    >
                      Hapus
                    </Button>
                  </div>
                ) : (
                  <div className="h-32 max-w-xs rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-slate-400">
                    <Image size={28} className="mb-2" />
                    <p className="text-xs">Belum ada gambar utama</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <label className="inline-flex">
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleUploadProductImage(e, 'thumbnail')} disabled={uploadingImage} />
                    <Button type="button" variant="outline" disabled={uploadingImage} asChild>
                      <span>{uploadingImage ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}{uploadingImage ? 'Mengupload...' : 'Upload Gambar'}</span>
                    </Button>
                  </label>
                  <label className="inline-flex">
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleUploadProductImage(e, 'gallery')} disabled={uploadingImage} />
                    <Button type="button" variant="outline" disabled={uploadingImage} asChild>
                      <span><Upload size={16} className="mr-2" />Tambah ke Galeri</span>
                    </Button>
                  </label>
                </div>
              </div>
              <div>
                <Label>URL Thumbnail</Label>
                <Input 
                  value={form.thumbnail_url} 
                  onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} 
                  placeholder="https://... atau /api/uploads/..."
                />
              </div>
              <div>
                <Label>URL Video (YouTube/Vimeo)</Label>
                <Input 
                  value={form.video_url} 
                  onChange={(e) => setForm({ ...form, video_url: e.target.value })} 
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              <div>
                <Label>URL Gambar Tambahan (pisahkan dengan koma)</Label>
                <Textarea 
                  value={form.images?.join(',') || ''} 
                  onChange={(e) => setForm({ ...form, images: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} 
                  rows={3}
                  placeholder="https://image1.jpg, https://image2.jpg"
                />
              </div>

              {/* Preview */}
              {(form.thumbnail_url || form.images?.length > 0) && (
                <div>
                  <Label>Preview</Label>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {form.thumbnail_url && (
                      <div className="w-20 h-20 border rounded overflow-hidden">
                        <img src={resolveMediaUrl(form.thumbnail_url)} alt="Thumbnail" className="w-full h-full object-cover" />
                      </div>
                    )}
                    {form.images?.map((img, i) => (
                      <div key={i} className="w-20 h-20 border rounded overflow-hidden relative group">
                        <img src={resolveMediaUrl(img)} alt={`Image ${i}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          className="absolute top-1 right-1 hidden group-hover:flex w-5 h-5 items-center justify-center rounded-full bg-black/60 text-white"
                          onClick={() => setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) })}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">SEO</h4>
                <div className="space-y-3">
                  <div>
                    <Label>SEO Title</Label>
                    <Input 
                      value={form.seo_title} 
                      onChange={(e) => setForm({ ...form, seo_title: e.target.value })} 
                      placeholder="Judul untuk mesin pencari"
                    />
                  </div>
                  <div>
                    <Label>SEO Description</Label>
                    <Textarea 
                      value={form.seo_description} 
                      onChange={(e) => setForm({ ...form, seo_description: e.target.value })} 
                      rows={2}
                      placeholder="Deskripsi untuk mesin pencari (maks 160 karakter)"
                      maxLength={160}
                    />
                  </div>
                  <div>
                    <Label>Slug URL</Label>
                    <Input 
                      value={form.slug} 
                      onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} 
                      placeholder="nama-produk-anda"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Batal
            </Button>
            <Button onClick={handleSave} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="save-product-btn">
              Simpan Produk
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Serial Keys Dialog */}
      <Dialog open={serialKeyDialog} onOpenChange={setSerialKeyDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key size={18} /> Serial Keys — {serialKeyProduct?.name}
            </DialogTitle>
          </DialogHeader>
          
          {serialKeyData && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{serialKeyData.total}</p>
                  <p className="text-xs text-muted-foreground">Total Keys</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{serialKeyData.available}</p>
                  <p className="text-xs text-muted-foreground">Tersedia</p>
                </div>
                <div className="bg-violet-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-violet-600">{serialKeyData.assigned}</p>
                  <p className="text-xs text-muted-foreground">Terpakai</p>
                </div>
              </div>

              <div>
                <Label>Tambah Serial Keys (satu per baris)</Label>
                <Textarea 
                  value={newSerialKeys}
                  onChange={(e) => setNewSerialKeys(e.target.value)}
                  rows={4}
                  placeholder={"XXXX-XXXX-XXXX-0001\nXXXX-XXXX-XXXX-0002\nXXXX-XXXX-XXXX-0003"}
                />
                <Button onClick={addSerialKeys} size="sm" className="mt-2 bg-[#2D6CDF] hover:bg-[#1A5BC4]">
                  <Plus size={14} className="mr-1" /> Tambah Keys
                </Button>
              </div>

              {serialKeyData.keys.length > 0 && (
                <div>
                  <Label>Daftar Serial Keys</Label>
                  <div className="border rounded-lg divide-y max-h-60 overflow-y-auto mt-1">
                    {serialKeyData.keys.map((key, i) => {
                      const isAssigned = serialKeyData.assigned_keys.some(a => a.serial_key === key);
                      const assignedTo = serialKeyData.assigned_keys.find(a => a.serial_key === key);
                      return (
                        <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div className="flex items-center gap-2">
                            <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{key}</code>
                            {isAssigned ? (
                              <Badge variant="outline" className="bg-violet-50 text-violet-700 text-xs">
                                Terpakai — {assignedTo?.customer_email || ''}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-xs">Tersedia</Badge>
                            )}
                          </div>
                          {!isAssigned && (
                            <Button variant="ghost" size="icon" onClick={() => deleteSerialKey(key)}>
                              <Trash2 size={12} className="text-red-500" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
